import Alert from 'components/Alert';
import { Button } from 'components/Button';
import { MultiSelect } from 'components/MultiSelect';
import { Toggle } from 'components/Toggle';
import { useStore } from 'context/store-context';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { StoreCreditOptions } from '../StoreCredit';
import { useUser } from 'context/user-context';
import useGodUser from 'hooks/useGodUser';
import { apiErrorHandler, NotificationTypes, SizeEnum } from 'utils';
import { withNotification } from 'components/Notification';
import { useMutation } from '@apollo/client';
import { UPDATE_STORE } from 'gql/mutations';
import SettingsViewWrapper from 'components/SettingsWrapperView';
import classNames from 'classnames';
import { UnsavedChangesGuard } from 'components/UnsavedChangesGuard';
import { useSettingsSaveState } from 'hooks/useSettingsSaveState';

const CUSTOM_RESOLUTION_NOTE_MAX_LENGTH = 200;

const Resolutions = withNotification(
  ({ showNotification }: { showNotification: any }) => {
    const navigate = useNavigate();
    const { storeId, storeProperties } = useStore();
    const user = useUser();

    const isGodUser = useGodUser();
    const shouldUpdateAppInstall = storeProperties?.shouldUpdateAppInstall;

    const shouldShowCustomResolutionEdit = useMemo(() => {
      const isAdmin = user.role === 'ADMIN';
      return isGodUser || isAdmin;
    }, [user, isGodUser]);
    const [teamData, setTeamData] = useState({
      enableStoreCredit: false,
      storeCreditOptions: [],
      requirePreferredResolution: false,
      isPreferredResolutionMandatory: false,
      isLateClaimCauseEnabled: true,
      isStolenClaimCauseEnabled: true,
      requireVideoUpload: false,
      isVideoUploadMandatory: false,
    });
    const onRedirectToPermissions = () => {
      navigate('/settings/integration');
    };

    const [customResolutionNotes, setCustomResolutionNotes] = useState({
      reorder: {
        enabled: false,
        text: '',
      },
      refund: {
        enabled: false,
        text: '',
      },
    });

    const handleCustomNoteEnabled = (field: 'reorder' | 'refund', v: boolean) =>
      setCustomResolutionNotes((prev) => ({
        ...prev,
        [field]: {
          ...prev[field],
          enabled: v,
        },
      }));

    const handleCustomNoteText = (
      field: 'refund' | 'reorder',
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const { value: text } = event.target;
      setCustomResolutionNotes((customResolutionNotes) => ({
        ...customResolutionNotes,
        [field]: {
          ...customResolutionNotes[field],
          text,
        },
      }));
    };

    useEffect(() => {
      if (storeProperties?.customResolutionNotes) {
        const reorder = storeProperties?.customResolutionNotes?.reorder;
        const refund = storeProperties?.customResolutionNotes?.refund;
        setCustomResolutionNotes({
          reorder: {
            enabled: Boolean(reorder?.enabled),
            text:
              typeof reorder?.text === 'string'
                ? reorder?.text
                : 'Re-ordered by ShipAid',
          },
          refund: {
            enabled: Boolean(refund?.enabled),
            text:
              typeof refund?.text === 'string'
                ? refund?.text
                : 'Refunded by ShipAid',
          },
        });
      }
    }, [storeProperties?.customResolutionNotes]);

    useEffect(() => {
      if (storeProperties) {
        setTeamData({
          requirePreferredResolution:
            storeProperties.resolvePortalOptions.requirePreferredResolution ??
            false,
          isPreferredResolutionMandatory:
            storeProperties.resolvePortalOptions
              .isPreferredResolutionMandatory ?? false,
          isLateClaimCauseEnabled:
            storeProperties.resolvePortalOptions.isLateClaimCauseEnabled ??
            true,
          isStolenClaimCauseEnabled:
            storeProperties.resolvePortalOptions.isStolenClaimCauseEnabled ??
            true,
          enableStoreCredit:
            storeProperties.resolvePortalOptions.enableStoreCredit ?? false,
          storeCreditOptions:
            storeProperties.resolvePortalOptions.storeCreditOptions ?? [],
          requireVideoUpload:
            storeProperties.resolvePortalOptions.requireVideoUpload ?? false,
          isVideoUploadMandatory:
            storeProperties.resolvePortalOptions.isVideoUploadMandatory ??
            false,
        });
      }
    }, [storeProperties]);

    const resolutionsFormState = useMemo(
      () => ({ teamData, customResolutionNotes }),
      [customResolutionNotes, teamData],
    );
    const resolutionsResetKey = useMemo(
      () =>
        JSON.stringify({
          resolvePortalOptions: storeProperties?.resolvePortalOptions ?? null,
          customResolutionNotes: storeProperties?.customResolutionNotes ?? null,
        }),
      [
        storeProperties?.customResolutionNotes,
        storeProperties?.resolvePortalOptions,
      ],
    );
    const { hasUnsavedChanges, markSaved, markSaveFailed } =
      useSettingsSaveState(resolutionsFormState, resolutionsResetKey);

    const [
      updateStore,
      { error: updateStoreError, loading: updateStoreLoading },
    ] = useMutation(UPDATE_STORE, {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    });
    const handleUpdateStore = async (): Promise<boolean> => {
      if (teamData.enableStoreCredit && !teamData.storeCreditOptions?.length) {
        markSaveFailed();
        showNotification(
          NotificationTypes.error,
          'Please select atleast 1 store credit option',
        );
        return false;
      }

      try {
        await updateStore({
          variables: {
            storeId,
            set: {
              resolvePortalOptions: {
                requirePreferredResolution: teamData.requirePreferredResolution,
                isPreferredResolutionMandatory:
                  teamData.isPreferredResolutionMandatory,
                isLateClaimCauseEnabled: teamData.isLateClaimCauseEnabled,
                isStolenClaimCauseEnabled: teamData.isStolenClaimCauseEnabled,
                enableStoreCredit: teamData.enableStoreCredit,
                storeCreditOptions: teamData.storeCreditOptions,
                requireVideoUpload: teamData.requireVideoUpload,
                isVideoUploadMandatory: teamData.isVideoUploadMandatory,
              },
              customResolutionNotes,
            },
          },
        });
        markSaved();
        showNotification(
          NotificationTypes.success,
          'Resolutions details updated',
        );
        return true;
      } catch (error) {
        markSaveFailed();
        showNotification(
          NotificationTypes.error,
          updateStoreError ? updateStoreError.message : 'Details not updated',
        );
        return false;
      }
    };

    const storeCreditOptionDisabled = useMemo(() => {
      return !(
        storeProperties?.isStoreShopifyPlus ||
        storeProperties?.features?.[StoreCreditOptions.StoreCredit]
      );
    }, [storeProperties]);

    return (
      <SettingsViewWrapper
        sectionId="resolution-portal"
        activeNav="resolution-portal/resolutions"
      >
        <UnsavedChangesGuard
          hasUnsavedChanges={hasUnsavedChanges}
          onSave={handleUpdateStore}
        />
        <div className="w-full h-full bg-[#f6f6f7]">
          <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
            {/* Header */}
            <header className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-[28px] font-semibold text-[#202223] leading-tight mb-2">
                  Resolutions Settings
                </h1>
                <p className="text-[#6d7175] text-sm max-w-2xl">
                  Configure resolution options, issue causes, and custom notes
                  for the Resolution Portal.
                </p>
              </div>
              <div className="flex-shrink-0">
                <Button
                  onClick={handleUpdateStore}
                  size={SizeEnum.md}
                  className="px-4 py-2 bg-[#2563EB] text-white text-sm font-medium rounded-md hover:bg-[#1d4ed8] transition-colors"
                  isLoading={updateStoreLoading}
                >
                  Save
                </Button>
              </div>
            </header>

            {shouldUpdateAppInstall && (
              <Alert
                message="You need to upgrade Shopify permissions before using store credits"
                type="warning"
                actions={[
                  <Button
                    key={'action'}
                    onClick={onRedirectToPermissions}
                    type="button"
                  >
                    Upgrade Permissions
                  </Button>,
                ]}
              />
            )}

            {/* Card: Resolution Options */}
            <section className="bg-white rounded-lg border border-[#e1e3e5] p-6 space-y-6">
              <h2 className="text-base font-semibold text-[#202223]">
                Resolution Options
              </h2>

              <div className="space-y-5">
                {/* Enable Store Credit */}
                <div>
                  <div className="flex items-center justify-between py-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-[#202223] mb-1">
                        Enable Store Credit
                      </label>
                      <p className="text-xs text-[#6d7175]">
                        Allow customers to receive store credit as a resolution
                        option
                      </p>
                    </div>
                    <Toggle
                      isEnabled={teamData.enableStoreCredit}
                      onChange={(v: boolean) => {
                        if (shouldUpdateAppInstall) {
                          return onRedirectToPermissions();
                        }
                        setTeamData({
                          ...teamData,
                          enableStoreCredit: v,
                          storeCreditOptions: !v
                            ? []
                            : teamData.storeCreditOptions,
                        });
                      }}
                      disabled={storeCreditOptionDisabled}
                    />
                  </div>

                  {/* Store Credit Options - Subsetting */}
                  {teamData.enableStoreCredit && (
                    <div className="relative">
                      {/* Elbow curve: L-shaped connector starting at border */}
                      <div className="absolute left-4 -top-px">
                        <svg width="16" height="24" viewBox="0 0 16 24">
                          {/* Vertical line down from border */}
                          <line
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="20"
                            stroke="#2563EB"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          {/* Horizontal line right */}
                          <line
                            x1="0"
                            y1="20"
                            x2="16"
                            y2="20"
                            stroke="#2563EB"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>

                      <div className="py-3 pl-12">
                        <label className="block text-sm font-medium text-[#202223] mb-2">
                          Store Credit Options
                        </label>
                        <MultiSelect
                          placeholder="Select Options"
                          selected={teamData.storeCreditOptions}
                          setSelected={(options: any[]) =>
                            setTeamData({
                              ...teamData,
                              storeCreditOptions: options as never[],
                            })
                          }
                          options={Object.values(StoreCreditOptions).map(
                            (c) => ({
                              label: c.replace(/_/g, ' '),
                              val: c,
                              disabled:
                                (!storeProperties?.isStoreShopifyPlus &&
                                  c === StoreCreditOptions.GiftCard) ||
                                (c === StoreCreditOptions.StoreCredit &&
                                  !storeProperties?.features?.[
                                    StoreCreditOptions.StoreCredit
                                  ]),
                            }),
                          )}
                          style={{ width: '100%', maxWidth: '400px' }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Preferred Resolution */}
                <div>
                  <div className="flex items-center justify-between py-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-[#202223] mb-1">
                        Preferred Resolution
                      </label>
                      <p className="text-xs text-[#6d7175]">
                        Allow customers to select their preferred resolution
                        method
                      </p>
                    </div>
                    <Toggle
                      isEnabled={teamData.requirePreferredResolution}
                      onChange={(v: boolean) =>
                        setTeamData({
                          ...teamData,
                          requirePreferredResolution: v,
                          isPreferredResolutionMandatory: !v
                            ? false
                            : teamData.isPreferredResolutionMandatory,
                        })
                      }
                    />
                  </div>

                  {/* Mandatory Preferred Resolution - Subsetting */}
                  {teamData.requirePreferredResolution && (
                    <div className="relative">
                      {/* Elbow curve: L-shaped connector starting at border */}
                      <div className="absolute left-4 -top-px">
                        <svg width="16" height="24" viewBox="0 0 16 24">
                          {/* Vertical line down from border */}
                          <line
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="20"
                            stroke="#2563EB"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          {/* Horizontal line right */}
                          <line
                            x1="0"
                            y1="20"
                            x2="16"
                            y2="20"
                            stroke="#2563EB"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>

                      <div className="flex items-center justify-between py-3 pl-12">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-[#202223] mb-1">
                            Mandatory Preferred Resolution
                          </label>
                          <p className="text-xs text-[#6d7175]">
                            Require customers to select a preferred resolution
                            before submitting
                          </p>
                        </div>
                        <Toggle
                          isEnabled={teamData.isPreferredResolutionMandatory}
                          onChange={(v: boolean) =>
                            setTeamData({
                              ...teamData,
                              isPreferredResolutionMandatory: v,
                            })
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Card: Issue Causes */}
            <section className="bg-white rounded-lg border border-[#e1e3e5] p-6 space-y-6">
              <h2 className="text-base font-semibold text-[#202223]">
                Issue Causes
              </h2>

              <div className="space-y-5">
                {/* Issue Cause: Late */}
                <div className="flex items-center justify-between py-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-[#202223] mb-1">
                      Issue Cause: Late
                    </label>
                    <p className="text-xs text-[#6d7175]">
                      Allow customers to select &quot;Late&quot; as an issue
                      cause
                    </p>
                  </div>
                  <Toggle
                    isEnabled={teamData.isLateClaimCauseEnabled}
                    onChange={(v: boolean) =>
                      setTeamData({
                        ...teamData,
                        isLateClaimCauseEnabled: v,
                      })
                    }
                  />
                </div>

                {/* Issue Cause: Stolen */}
                <div className="flex items-center justify-between py-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-[#202223] mb-1">
                      Issue Cause: Stolen
                    </label>
                    <p className="text-xs text-[#6d7175]">
                      Allow customers to select &quot;Stolen&quot; as an issue
                      cause
                    </p>
                  </div>
                  <Toggle
                    isEnabled={teamData.isStolenClaimCauseEnabled}
                    onChange={(v: boolean) =>
                      setTeamData({
                        ...teamData,
                        isStolenClaimCauseEnabled: v,
                      })
                    }
                  />
                </div>
              </div>
            </section>

            {/* Card: Media Settings */}
            <section className="bg-white rounded-lg border border-[#e1e3e5] p-6 space-y-6">
              <h2 className="text-base font-semibold text-[#202223]">
                Media Settings
              </h2>

              <div className="space-y-5">
                {/* Video Upload */}
                <div>
                  <div className="flex items-center justify-between py-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-[#202223] mb-1">
                        Video Upload
                      </label>
                      <p className="text-xs text-[#6d7175]">
                        Allow customers to upload videos when filing a claim
                      </p>
                    </div>
                    <Toggle
                      isEnabled={teamData.requireVideoUpload}
                      onChange={(v: boolean) =>
                        setTeamData({
                          ...teamData,
                          requireVideoUpload: v,
                          isVideoUploadMandatory: !v
                            ? false
                            : teamData.isVideoUploadMandatory,
                        })
                      }
                    />
                  </div>

                  {/* Video Upload Mandatory - Subsetting */}
                  {teamData.requireVideoUpload && (
                    <div className="relative">
                      {/* Elbow curve: L-shaped connector starting at border */}
                      <div className="absolute left-4 -top-px">
                        <svg width="16" height="24" viewBox="0 0 16 24">
                          {/* Vertical line down from border */}
                          <line
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="20"
                            stroke="#2563EB"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          {/* Horizontal line right */}
                          <line
                            x1="0"
                            y1="20"
                            x2="16"
                            y2="20"
                            stroke="#2563EB"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>

                      <div className="flex items-center justify-between py-3 pl-12">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-[#202223] mb-1">
                            Video Upload Mandatory
                          </label>
                          <p className="text-xs text-[#6d7175]">
                            Require customers to upload a video before
                            submitting a claim
                          </p>
                        </div>
                        <Toggle
                          isEnabled={teamData.isVideoUploadMandatory}
                          onChange={(v: boolean) =>
                            setTeamData({
                              ...teamData,
                              isVideoUploadMandatory: v,
                            })
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Card: Custom Resolution Notes */}
            {shouldShowCustomResolutionEdit && (
              <section className="bg-white rounded-lg border border-[#e1e3e5] p-6 space-y-6">
                <h2 className="text-base font-semibold text-[#202223]">
                  Custom Resolution Notes
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* ReOrder */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-[#202223]">
                        ReOrder
                      </label>
                      <Toggle
                        isEnabled={customResolutionNotes.reorder.enabled}
                        onChange={handleCustomNoteEnabled.bind(null, 'reorder')}
                      />
                    </div>
                    <input
                      type="text"
                      value={customResolutionNotes?.reorder?.text}
                      onChange={handleCustomNoteText.bind(null, 'reorder')}
                      maxLength={CUSTOM_RESOLUTION_NOTE_MAX_LENGTH}
                      disabled={!customResolutionNotes.reorder.enabled}
                      className={classNames(
                        'w-full border border-[#c9cccf] rounded-md px-3 py-2 text-sm text-[#202223] focus:outline-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] transition-all',
                        customResolutionNotes.reorder.enabled
                          ? 'bg-white hover:border-[#a6acb3]'
                          : 'bg-[#f6f6f7] cursor-not-allowed text-[#6d7175]',
                      )}
                      placeholder="Re-ordered by ShipAid"
                    />
                  </div>

                  {/* Refund */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-[#202223]">
                        Refund
                      </label>
                      <Toggle
                        isEnabled={customResolutionNotes.refund.enabled}
                        onChange={handleCustomNoteEnabled.bind(null, 'refund')}
                      />
                    </div>
                    <input
                      type="text"
                      value={customResolutionNotes?.refund?.text}
                      disabled={!customResolutionNotes.refund.enabled}
                      onChange={handleCustomNoteText.bind(null, 'refund')}
                      maxLength={CUSTOM_RESOLUTION_NOTE_MAX_LENGTH}
                      className={classNames(
                        'w-full border border-[#c9cccf] rounded-md px-3 py-2 text-sm text-[#202223] focus:outline-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] transition-all',
                        customResolutionNotes.refund.enabled
                          ? 'bg-white hover:border-[#a6acb3]'
                          : 'bg-[#f6f6f7] cursor-not-allowed text-[#6d7175]',
                      )}
                      placeholder="Refunded by ShipAid"
                    />
                  </div>
                </div>
              </section>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-[#e1e3e5]">
              <Button
                onClick={handleUpdateStore}
                size={SizeEnum.md}
                className="px-4 py-2 bg-[#2563EB] text-white text-sm font-medium rounded-md hover:bg-[#1d4ed8] transition-colors"
                isLoading={updateStoreLoading}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </SettingsViewWrapper>
    );
  },
);

export default Resolutions;
